import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardList, Filter } from 'lucide-react'
import { useProposals } from '@/hooks/useProposals'
import type { ProposalStatus } from '@/types/api'
import { cn } from '@/lib/utils'

const statusOptions: Array<{ value: ProposalStatus | 'all'; label: string }> = [
  { value: 'all', label: 'すべて' },
  { value: 'pending', label: '承認待ち' },
  { value: 'approved', label: '承認済み' },
  { value: 'rejected', label: '却下' },
  { value: 'expired', label: '期限切れ' },
  { value: 'executed', label: '実行済み' },
  { value: 'failed', label: '失敗' },
  { value: 'draft', label: '下書き' },
]

const statusColor: Record<ProposalStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  executed: 'bg-blue-100 text-blue-800',
  expired: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-800',
  draft: 'bg-gray-100 text-gray-600',
}

function formatDate(value?: string) {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

export function ProposalsPage() {
  const [statusFilter, setStatusFilter] = useState<ProposalStatus | 'all'>('all')

  const filters = useMemo(() => {
    if (statusFilter === 'all') return undefined
    return { status: [statusFilter] as ProposalStatus[] }
  }, [statusFilter])

  const { data, isLoading, error } = useProposals(filters)

  const proposals = data?.items ?? []

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">提案一覧</h1>
        <p className="text-muted-foreground">
          Context Bank から生成された提案と根拠を一覧で確認できます。
        </p>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          ステータス
        </div>
        <div className="flex flex-wrap gap-2">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setStatusFilter(option.value)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                statusFilter === option.value
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-muted bg-background text-muted-foreground hover:bg-muted'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border bg-card shadow-sm">
        <div className="flex items-center gap-2 border-b px-6 py-4">
          <ClipboardList className="h-5 w-5 text-primary" />
          <h2 className="font-semibold">提案リスト</h2>
        </div>
        <div className="p-6">
          {isLoading && (
            <div className="text-sm text-muted-foreground">読み込み中...</div>
          )}
          {error && (
            <div className="text-sm text-red-600">
              提案の取得に失敗しました
            </div>
          )}
          {!isLoading && proposals.length === 0 && (
            <div className="text-sm text-muted-foreground">提案はまだありません。</div>
          )}

          <div className="space-y-4">
            {proposals.map((proposal) => (
              <Link
                key={proposal.id}
                to={`/proposals/${proposal.id}`}
                className="block rounded-lg border p-4 transition-colors hover:bg-muted/30"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="space-y-1">
                    <h3 className="font-semibold">
                      {proposal.title ?? '無題の提案'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {proposal.summary ?? 'サマリーが未設定です'}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'rounded-full px-2 py-1 text-xs font-medium',
                      statusColor[proposal.status]
                    )}
                  >
                    {proposal.status}
                  </span>
                </div>
                <div className="mt-3 grid gap-2 text-xs text-muted-foreground md:grid-cols-4">
                  <div>優先度: {proposal.priority}</div>
                  <div>スコープ: {proposal.actionScope}</div>
                  <div>リスク: {proposal.riskLevel}</div>
                  <div>作成日: {formatDate(proposal.createdAt)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
